import clsx from 'clsx';
import React from 'react';
import type { ControlProps, DropdownIndicatorProps, OptionProps, Props } from 'react-select';
import ReactSelect, { components, StylesConfig } from 'react-select';

import { Icon } from '@/ui/Icon';

const selectStyles: StylesConfig = {
	control: (_, props) => ({
		borderColor: '#EAECF3',
		borderBottomColor: props.selectProps?.menuIsOpen ? 'transparent' : '',
	}),
	dropdownIndicator: () => ({
		color: '#E4E8F0',
	}),
	option: (provided) => ({
		...provided,
		className: 'py-0.5 cursor-pointer',
	}),
	menuList: (provided) => ({
		...provided,
		maxHeight: 150,
	}),
};

const ValueContainer = ({ children, ...rest }) => (
	<div {...rest} className="flex select-none">
		{children}
	</div>
);

const IndicatorsContainer = ({ children, ...rest }) => (
	<div {...rest} className="flex">
		{children}
	</div>
);

const Control = (props: ControlProps) => {
	const { menuIsOpen } = props;

	return (
		<components.Control
			{...props}
			className={clsx('cursor-pointer flex gap-2 bg-white border rounded pl-2 pr-4 pt-1.5 pb-1 text-xs w-32', {
				'rounded-b-none': menuIsOpen,
			})}
		/>
	);
};

const DropdownIndicator = (props: DropdownIndicatorProps) => {
	const { selectProps } = props;

	return (
		<components.DropdownIndicator
			{...props}
			className={clsx('flex items-center justify-center size-7 p-0 absolute right-0 top-0')}
		>
			<Icon
				name="expandChevron"
				size={8}
				className={clsx('transition-transform duration-300', {
					'rotate-180': selectProps?.menuIsOpen,
				})}
			/>
		</components.DropdownIndicator>
	);
};

const Option = ({ innerRef, innerProps, children, isSelected }: OptionProps) => (
	<div
		ref={innerRef}
		{...innerProps}
		className={clsx('text-xs py-1 px-2 cursor-pointer hover:bg-gray-100', {
			'!bg-gray-300': isSelected,
		})}
	>
		{children}
	</div>
);

const Menu = (props) => (
	<components.Menu
		{...props}
		className="absolute bg-white w-full !border !border-t-0 !rounded-b !rounded-t-none !shadow-none z-10 !m-0"
	/>
);

const Select = ({ value, defaultValue, options, onChange, isSearchable }: Props) => (
	<ReactSelect
		value={value}
		components={{
			ValueContainer,
			IndicatorsContainer,
			Control,
			DropdownIndicator,
			Option,
			Menu,
			IndicatorSeparator: null,
		}}
		styles={selectStyles}
		isSearchable={isSearchable}
		defaultValue={defaultValue}
		options={options}
		onChange={onChange}
	/>
);

export { Select };
